import { useCallback, useEffect, useRef, useState } from 'react';
import { appendStreamingContent } from './streamUiReducer';

interface UsePilotStreamTransportOptions {
  flushDelayMs: number;
  onFlush: (content: string) => void;
}

export function usePilotStreamTransport({
  flushDelayMs,
  onFlush,
}: UsePilotStreamTransportOptions) {
  const chatStreamAbortRef = useRef<AbortController | null>(null);
  const pilotRecoveryStreamAbortRef = useRef<AbortController | null>(null);
  const [isChatStreamActive, setIsChatStreamActive] = useState(false);
  const [isRecoveryStreamActive, setIsRecoveryStreamActive] = useState(false);
  const pilotStreamChunkBufferRef = useRef('');
  const pilotStreamChunkFlushTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const flushPilotStreamChunkBuffer = useCallback(
    (options: { discard?: boolean } = {}) => {
      if (pilotStreamChunkFlushTimeoutRef.current) {
        clearTimeout(pilotStreamChunkFlushTimeoutRef.current);
        pilotStreamChunkFlushTimeoutRef.current = null;
      }

      const buffered = pilotStreamChunkBufferRef.current;
      pilotStreamChunkBufferRef.current = '';

      if (options.discard || buffered.length === 0) {
        return;
      }

      onFlush(buffered);
    },
    [onFlush],
  );

  const queuePilotStreamChunk = useCallback(
    (content: string, options: { flushNow?: boolean } = {}) => {
      if (!content) {
        return;
      }

      pilotStreamChunkBufferRef.current = appendStreamingContent(
        pilotStreamChunkBufferRef.current,
        content,
      );

      if (options.flushNow) {
        flushPilotStreamChunkBuffer();
        return;
      }

      if (pilotStreamChunkFlushTimeoutRef.current) {
        return;
      }

      pilotStreamChunkFlushTimeoutRef.current = setTimeout(() => {
        pilotStreamChunkFlushTimeoutRef.current = null;
        flushPilotStreamChunkBuffer();
      }, flushDelayMs);
    },
    [flushDelayMs, flushPilotStreamChunkBuffer],
  );

  const abortChatStream = useCallback(() => {
    if (chatStreamAbortRef.current) {
      chatStreamAbortRef.current.abort();
      chatStreamAbortRef.current = null;
    }
    setIsChatStreamActive(false);
  }, []);

  const abortRecoveryStream = useCallback(() => {
    if (pilotRecoveryStreamAbortRef.current) {
      pilotRecoveryStreamAbortRef.current.abort();
      pilotRecoveryStreamAbortRef.current = null;
    }
    setIsRecoveryStreamActive(false);
  }, []);

  const runChatStream = useCallback(
    async <T>(runner: (controller: AbortController) => Promise<T>) => {
      const controller = new AbortController();
      chatStreamAbortRef.current = controller;
      setIsChatStreamActive(true);

      try {
        return await runner(controller);
      } finally {
        if (chatStreamAbortRef.current === controller) {
          chatStreamAbortRef.current = null;
        }
        setIsChatStreamActive(false);
      }
    },
    [],
  );

  const runRecoveryStream = useCallback(
    async <T>(runner: (controller: AbortController) => Promise<T>) => {
      const controller = new AbortController();
      pilotRecoveryStreamAbortRef.current = controller;
      setIsRecoveryStreamActive(true);

      try {
        return await runner(controller);
      } finally {
        if (pilotRecoveryStreamAbortRef.current === controller) {
          pilotRecoveryStreamAbortRef.current = null;
        }
        setIsRecoveryStreamActive(false);
      }
    },
    [],
  );

  const clearTransport = useCallback(
    (options: { discardChunks?: boolean } = {}) => {
      abortChatStream();
      abortRecoveryStream();
      flushPilotStreamChunkBuffer({ discard: options.discardChunks });
    },
    [abortChatStream, abortRecoveryStream, flushPilotStreamChunkBuffer],
  );

  useEffect(() => {
    return () => {
      clearTransport({ discardChunks: true });
    };
  }, [clearTransport]);

  return {
    chatStreamAbortRef,
    pilotRecoveryStreamAbortRef,
    isChatStreamActive,
    isRecoveryStreamActive,
    pilotStreamChunkBufferRef,
    pilotStreamChunkFlushTimeoutRef,
    flushPilotStreamChunkBuffer,
    queuePilotStreamChunk,
    abortChatStream,
    abortRecoveryStream,
    runChatStream,
    runRecoveryStream,
    clearTransport,
  };
}
